import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components";
import { CALENDLY_URL, CONTACT_EMAIL } from "@/constants";
import { useTranslation } from "@/i18n";

export const Contact: React.FC = () => {
  const t = useTranslation();
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  return (
    <>
      <div id="kontakt"></div>
      <div className="bg-[#1d2128] py-[72px] md:py-[120px] relative overflow-hidden">
        {/* Cyan glow behind the widget */}
        <div className="pointer-events-none absolute right-[-120px] top-1/2 -translate-y-1/2 h-[480px] w-[480px] rounded-full bg-[#01C6FB]/15 blur-3xl" />

        <div className="max-w-screen-xl m-auto px-[38px] 2xl:px-0 relative">
          <div className="flex flex-col lg:flex-row gap-[48px] lg:gap-[80px]">
            <div className="w-full lg:w-1/2">
              <p className="text-[#01C6FB] font-semibold tracking-[0.18em] uppercase text-[13px] md:text-[14px]">
                {t.contact.overline}
              </p>
              <h2 className="mt-3 text-white font-bold text-[28px] leading-[38px] md:text-[40px] md:leading-[52px]">
                {t.contact.headlinePlain}{" "}
                <span className="text-[#01C6FB]">
                  {t.contact.headlineAccent}
                </span>
              </h2>
              <p className="mt-5 text-[16px] leading-[26px] md:text-[18px] md:leading-[30px] text-base-300">
                {t.contact.intro}
              </p>

              <p className="mt-8 text-[13px] font-semibold tracking-[0.15em] uppercase text-base-400">
                {t.contact.takeawaysLabel}
              </p>
              <ul className="mt-4 space-y-3">
                {t.contact.takeaways.map((takeaway) => (
                  <li key={takeaway} className="flex items-start">
                    <span className="material-symbols-outlined mr-3 text-[22px] text-[#01C6FB]">
                      check_circle
                    </span>
                    <span className="text-[15px] leading-[25px] md:text-[16px] md:leading-[27px] text-base-200">
                      {takeaway}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-8 text-[15px] md:text-[16px] text-base-300">
                {t.contact.emailPrefix}{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-[#01C6FB] font-semibold hover:underline"
                >
                  {CONTACT_EMAIL}
                </a>{" "}
                {t.contact.emailSuffix}
              </p>
            </div>

            {/* Calendly card (click-to-load, GDPR-friendly) */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className="w-full lg:w-1/2"
            >
              <div className="rounded-[20px] bg-white shadow-2xl p-[24px] md:p-[28px]">
                {calendlyLoaded ? (
                  <iframe
                    src={`${CALENDLY_URL}?hide_gdpr_banner=1&embed_type=Inline&embed_domain=rocknitive.com`}
                    title={t.contact.calendlyTitle}
                    className="w-full h-[620px] rounded-[12px] border-0"
                  />
                ) : (
                  <div className="flex flex-col items-center text-center py-[48px] md:py-[64px] px-[8px]">
                    <span className="material-symbols-outlined flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#F2FAFC] text-[36px] text-[#01C6FB]">
                      calendar_month
                    </span>
                    <h3 className="mt-6 text-[20px] md:text-[22px] font-bold text-[#171A1F]">
                      {t.contact.cardTitle}
                    </h3>
                    <p className="mt-2 max-w-sm text-[15px] leading-[25px] text-base-600">
                      {t.contact.cardText}
                    </p>
                    <Button
                      shape="circle"
                      size="large"
                      className="mt-6 bg-[#01C6FB] text-[#10141a] font-semibold border-0 px-8 hover:bg-[#4dd7fc]"
                      onClick={() => setCalendlyLoaded(true)}
                    >
                      {t.contact.cardButton}
                    </Button>
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 text-[14px] text-base-500 hover:text-[#0195bd] hover:underline"
                    >
                      {t.contact.cardExternal}
                    </a>
                    <p className="mt-6 text-[12px] text-base-400 max-w-xs">
                      {t.contact.cardGdpr}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-5 text-center">
                <p className="text-[13px] md:text-[14px] text-base-300">
                  {t.contact.microTrust}
                </p>
                <p className="mt-2 text-[12px] md:text-[13px] text-base-400">
                  {t.contact.microReassurance}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};
